<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime } from '@yuksalish/ui'
import ReasonPromptModal from '../components/ReasonPromptModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney } from '../utils/format'
import type { PayrollPosting } from '../types/finance'

/**
 * `/payroll/postings` — posted net pay per employee plus the treasury outflow
 * transaction. Reversing creates a treasury inflow reversal.
 */

const { t } = useI18n()
const auth = useAuthStore()
const { listPayrollPostings, reversePayrollPosting } = useFinanceApi()

const PER_PAGE = 25
const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('payroll_postings.manage'))

const rows = ref<PayrollPosting[]>([])
const total = ref(0)
const totalPages = ref(1)
const page = ref(1)
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busy = ref(false)
const pending = ref<PayrollPosting | null>(null)

const filters = ref({ status: '', currency_code: '' })

const netTotals = computed(() => {
  const totals = new Map<string, number>()
  for (const row of rows.value) {
    const code = row.currency_code || '—'
    totals.set(code, (totals.get(code) ?? 0) + Number(row.net_amount ?? 0))
  }
  return [...totals.entries()]
    .map(([code, amount]) => ({ code, amount }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
})

const rangeLabel = computed(() =>
  total.value ? t('finance.common.rows', { count: total.value }) : '',
)

function statusLabel(status: string): string {
  const key = `finance.payrollFlow.postingStatus${status.charAt(0).toUpperCase()}${status.slice(1)}`
  const label = t(key)
  return label.startsWith('finance.') ? status : label
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listPayrollPostings({
      status: filters.value.status || undefined,
      currency_code: filters.value.currency_code.trim() || undefined,
      page: page.value,
      per_page: PER_PAGE,
    })
    rows.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
    rows.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

async function confirmReason(reason: string) {
  const posting = pending.value
  if (!posting) return
  busy.value = true
  actionError.value = ''
  actionMessage.value = ''
  try {
    await reversePayrollPosting(posting.id, reason)
    actionMessage.value = t('finance.payrollFlow.postings.doneReversed', { employee: posting.employee_name })
    pending.value = null
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busy.value = false
  }
}

watch(
  () => [filters.value.status, filters.value.currency_code],
  () => {
    page.value = 1
    void load()
  },
)

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('finance.payrollFlow.postings.eyebrow') }}</p>
      <h2>{{ t('finance.payrollFlow.postings.title') }}</h2>
      <p>{{ t('finance.payrollFlow.postings.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('finance.common.allStatuses') }}</option>
      <option value="posted">{{ statusLabel('posted') }}</option>
      <option value="paid">{{ statusLabel('paid') }}</option>
      <option value="reversed">{{ statusLabel('reversed') }}</option>
    </select>
    <input v-model="filters.currency_code" class="filter-select" type="text" placeholder="UZS">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.refresh') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <div v-if="netTotals.length" class="summary-row">
      <article v-for="entry in netTotals" :key="entry.code" class="summary-card accent">
        <span class="summary-value">{{ formatMoney(entry.amount, entry.code) }}</span>
        <span class="summary-label">{{ t('finance.payrollFlow.postings.net') }} · {{ entry.code }}</span>
      </article>
    </div>

    <p v-if="!rows.length" class="state-message">{{ t('finance.payrollFlow.postings.empty') }}</p>

    <template v-else>
      <div class="table-scroll">
        <table class="data-table">
          <thead>
            <tr>
              <th>{{ t('finance.payrollFlow.postings.employee') }}</th>
              <th>{{ t('finance.payrollFlow.postings.run') }}</th>
              <th>{{ t('finance.payrollFlow.postings.account') }}</th>
              <th class="cell-num">{{ t('finance.payrollFlow.postings.net') }}</th>
              <th>{{ t('finance.payrollFlow.postings.postedAt') }}</th>
              <th>{{ t('finance.common.status') }}</th>
              <th class="cell-actions">{{ t('finance.common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="posting in rows" :key="posting.id">
              <td class="cell-name">
                {{ posting.employee_name }}
                <span class="cell-sub">{{ posting.employee_code || '—' }}</span>
              </td>
              <td>
                <RouterLink class="link-action" :to="`/payroll/runs/${posting.payroll_run_id}`">
                  {{ posting.payroll_run_code || posting.payroll_run_id }}
                </RouterLink>
              </td>
              <td>
                {{ posting.financial_account_name || '—' }}
                <span class="cell-sub">
                  {{ posting.financial_account_code || '—' }} ·
                  {{ posting.financial_account_transaction_id || '—' }}
                </span>
              </td>
              <td class="cell-num">{{ formatMoney(posting.net_amount, posting.currency_code) }}</td>
              <td class="cell-date">
                {{ posting.posted_at ? formatDisplayDateTime(posting.posted_at) : '—' }}
              </td>
              <td>
                <span class="status-badge" :class="`status-${posting.status}`">
                  {{ statusLabel(posting.status) }}
                </span>
              </td>
              <td class="cell-actions">
                <button
                  v-if="canManage && posting.status !== 'reversed'"
                  class="link-action danger"
                  type="button"
                  :disabled="busy"
                  @click="pending = posting"
                >
                  {{ t('finance.payrollFlow.reverse') }}
                </button>
                <span v-else class="empty-inline">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <span>{{ rangeLabel }}</span>
        <button class="btn-secondary" type="button" :disabled="page <= 1" @click="page = page - 1; load()">
          {{ t('finance.common.previous') }}
        </button>
        <button
          class="btn-secondary"
          type="button"
          :disabled="page >= totalPages"
          @click="page = page + 1; load()"
        >
          {{ t('finance.common.next') }}
        </button>
      </div>
    </template>
  </template>

  <ReasonPromptModal
    :open="Boolean(pending)"
    :title="t('finance.payrollFlow.reverseTitle')"
    :message="t('finance.payrollFlow.reverseMessage')"
    :busy="busy"
    @close="pending = null"
    @confirm="confirmReason"
  />
</template>

<style scoped>
.status-posted,
.status-paid {
  border-color: #4e6651;
  color: #2f4a33;
  background: #eef4f0;
}

.status-reversed {
  border-color: #e0b0a6;
  color: #a13c2c;
  background: #fdf3f1;
}
</style>
